import {ChevronRight, ShieldOutlined} from '@mui/icons-material';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {FC} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {RoleDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import theme from 'src/theme';
import {IAM_TONE, IamTone, countColSx, kindColSx, listRowSx, nameColSx, pillSx, subjectBadgeSx} from '../iam.sx';
import {RoleKind, roleKind} from '../roleKind';

const KIND_LABEL: Record<RoleKind, string> = {
    builtIn: 'accessControlBuiltInRole',
    custom: 'accessControlCustomRole',
};

const KIND_TONE: Record<RoleKind, IamTone> = {
    builtIn: IAM_TONE.platform,
    custom: IAM_TONE.yours,
};

interface RoleRowProps {
    role: RoleDTO;
}

/**
 * One role, wherever roles are listed — the roles tab and the "who grants this" list on a permission.
 *
 * The three trailing pills are the whole reason this is a component rather than a line of text. What
 * someone is actually deciding when they look at a list of roles is "is this the one to put people
 * in", and that is answered by how much it grants, whether any of it is dangerous, and whether anyone
 * is in it already. A role nobody holds is the single most useful thing to surface when tidying up,
 * and it is invisible unless you say so.
 */
export const RoleRow: FC<RoleRowProps> = ({role}) => {
    const intl = useIntl();
    const navigate = useNavigate();

    const permissions = role.permissionCount ?? role.permissions?.length ?? 0;
    const holders = role.holders ?? 0;
    const kind = roleKind(role);
    const kindTooltip = kind === 'builtIn'
        ? intl.formatMessage({
            id: 'accessControlBuiltInRoleTooltip',
            defaultMessage:
                'New users of this kind start with this role, so it cannot be deleted. You can rename it and change what it grants.',
        })
        : '';

    return (
        <Box
            sx={{...listRowSx, cursor: 'pointer'}}
            onClick={() =>
                navigate(
                    AppRouteSpace([
                        AppRouteEnum.app,
                        AppRouteEnum.accessControl,
                        AppRouteEnum.roles,
                        String(role.id),
                    ])
                )
            }
        >
            <Box sx={subjectBadgeSx}>
                <ShieldOutlined fontSize="small"/>
            </Box>

            <Box sx={nameColSx}>
                <Typography noWrap sx={{fontWeight: 600, fontSize: '0.9375rem'}}>{role.name}</Typography>
                {role.description && (
                    <Typography
                        noWrap
                        sx={{fontSize: '0.8125rem', color: theme.palette.neutral[400]}}
                    >
                        {role.description}
                    </Typography>
                )}
            </Box>

            {/* Where the role came from, in a column rather than as a badge trailing the name: beside
                the name it moved with the length of the name, so it could not be read down the list -
                which is also what makes it filterable. */}
            <Box sx={kindColSx}>
                <Tooltip title={kindTooltip}>
                    <Typography sx={pillSx(KIND_TONE[kind])}>
                        {intl.formatMessage({id: KIND_LABEL[kind], defaultMessage: kind})}
                    </Typography>
                </Tooltip>
            </Box>

            <Box sx={{flex: 1}}/>

            {/* Two numbers in fixed columns rather than two pills at the end of the row, so they
                line up with their headings and with the same columns on the people tab. As pills they
                sat wherever the name before them happened to end, which is unreadable down a column
                and was the thing that made switching tabs feel like changing screens. */}
            <Typography sx={countColSx}>{permissions}</Typography>

            <Tooltip
                title={
                    holders === 0
                        ? intl.formatMessage({
                            id: 'accessControlNobodyTooltip',
                            defaultMessage:
                                'No one holds this role. It grants nothing to anyone until you assign it.',
                        })
                        : ''
                }
            >
                {/* Dimmed at zero rather than labelled "Nobody": the column already says what the
                    number counts, and a role nobody holds is worth spotting while scanning, which is
                    what the colour does without costing the alignment a word would. */}
                <Typography sx={{...countColSx, color: holders ? theme.palette.neutral[500] : theme.palette.neutral[300]}}>
                    {holders}
                </Typography>
            </Tooltip>

            <ChevronRight sx={{color: theme.palette.neutral[400]}}/>
        </Box>
    );
};
